/**
 * Tema claro/escuro.
 *
 * `tokens/colors.css` só conhece `[data-theme="dark"]`: não há bloco de
 * `prefers-color-scheme`. Então "Automático" não é um terceiro tema — é este
 * módulo escutando a preferência do sistema e ligando ou desligando o mesmo
 * atributo. A escolha fica no localStorage porque ela é da pessoa, não da
 * sessão: sair da conta não devolve ninguém ao tema claro.
 */

import { useEffect, useState } from 'react';

import type { Tema } from '@ds/navigation';

const CHAVE = 'vec.tema';

function escuroNoSistema(): boolean {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function temaSalvo(): Tema {
    try {
        const bruto = localStorage.getItem(CHAVE);
        if (bruto === 'claro' || bruto === 'escuro' || bruto === 'sistema') return bruto;
    } catch {
        /* Sem armazenamento: vale o padrão. */
    }
    return 'claro';
}

export function aplicarTema(tema: Tema): void {
    const escuro = tema === 'escuro' || (tema === 'sistema' && escuroNoSistema());
    const raiz = document.documentElement;
    if (escuro) raiz.setAttribute('data-theme', 'dark');
    else raiz.removeAttribute('data-theme');

    try {
        localStorage.setItem(CHAVE, tema);
    } catch {
        /* Tema só nesta aba. */
    }
}

/** Reage à troca no sistema operacional enquanto o tema for "sistema". */
export function observarSistema(tema: Tema, aoMudar: () => void): () => void {
    if (tema !== 'sistema' || typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => { };
    const consulta = window.matchMedia('(prefers-color-scheme: dark)');
    consulta.addEventListener('change', aoMudar);
    return () => consulta.removeEventListener('change', aoMudar);
}

/**
 * O tema **em vigor**, não o escolhido.
 *
 * A diferença importa para quem precisa do valor e não só do CSS: com
 * "sistema", `temaSalvo()` devolve `'sistema'`, que não diz se está escuro. O
 * que decide é o atributo que `aplicarTema` deixou na raiz — e é ele que este
 * hook lê e observa, para o desenho do fluxo virar junto com o resto da tela.
 */
export function useEscuro(): boolean {
    const [escuro, setEscuro] = useState(
        () => typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark',
    );

    useEffect(() => {
        const raiz = document.documentElement;
        const ler = () => setEscuro(raiz.getAttribute('data-theme') === 'dark');
        ler();
        const observador = new MutationObserver(ler);
        observador.observe(raiz, { attributes: true, attributeFilter: ['data-theme'] });
        return () => observador.disconnect();
    }, []);

    return escuro;
}
